import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import Unauthorized from "../../components/views/Unauthorized";
import { useAuth } from "../../hooks/useAuth";
import { AuthPermissionAction } from "./@types";

interface RequireAuthProps {
  allowedRoles?: string[];
  excludedRoles?: string[];
  requiredPermission?: string;
  requiredAction?: AuthPermissionAction;
}

const RequireAuth: React.FC<RequireAuthProps> = ({
  allowedRoles = [],
  excludedRoles = [],
  requiredPermission,
  requiredAction,
}) => {
  const { user, isAuthenticated, isLoading, hasPermission } = useAuth();

  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Custom roles can have any name (and may lack "Basic"), so routes that
  // rely on permissions only keep out the roles listed in excludedRoles.
  const userRoles = Array.isArray(user.roles) ? user.roles : [];

  if (
    excludedRoles.some((excluded) =>
      userRoles.some((role) => role.toLowerCase() === excluded.toLowerCase())
    )
  ) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (allowedRoles.length > 0) {
    const hasRequiredRole =
      userRoles.some((userRole: string) =>
        allowedRoles.some(
          (role) => userRole.toLowerCase() === role.toLowerCase()
        )
      ) ||
      (allowedRoles.includes("admin") && user?.isAdmin);

    if (!hasRequiredRole) {
      return <Navigate to="/sign-in" state={{ from: location }} replace />;
    }
  }

  // Check permission-based access
  if (
    requiredPermission &&
    !hasPermission(requiredPermission, requiredAction)
  ) {
    return <Unauthorized />;
  }

  return <Outlet />;
};

export default RequireAuth;
